import { ApolloError, useApolloClient } from "@apollo/client";
import { Send as SendIcon } from "@mui/icons-material";
import moment, { Moment } from "moment";
import { useSnackbar } from "notistack";
import { useOpenInvoiceMutation } from "../Invoices.generated.js";
import { useTranslate } from "@tolgee/react";
import { DEFAULT_INVOICE_PAYMENT_TERM_DURATION } from "../../../utils.js";
import { Button, Tooltip } from "@mui/material";
import {
  splitMissingFields,
  SERVICE_PERIOD_MISSING_KEY,
} from "../eInvoicePreconditionMessage.js";

const MISSING_FIELD_LABELS = {
  billingAddress: "missingField.billingAddress",
  format: "missingField.format",
  fullLegalName: "missingField.fullLegalName",
  iban: "missingField.iban",
  vatOrTaxNumber: "missingField.vatOrTaxNumber",
  deliveryOfService: "missingField.deliveryOfService",
} as const;

interface Props {
  invoiceId: string;
  projectId: string;
  docId: string;
  disabled?: boolean;
  disabledTooltip?: string;
  paymentTermDuration: number;
  deliveryOfServiceStart?: string | null;
  deliveryOfServiceEnd?: string | null;
}

export const OpenInvoiceButton = ({
  invoiceId,
  projectId,
  docId,
  disabled = false,
  disabledTooltip,
  paymentTermDuration,
  deliveryOfServiceStart,
  deliveryOfServiceEnd,
}: Props) => {
  const { t } = useTranslate(["InvoiceDetailsForm", "Invoices"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [openInvoice, { loading: isSubmittingOpenInvoice }] =
    useOpenInvoiceMutation({ client });

  const handleSubmit = async (values: FormValues) => {
    try {
      await openInvoice({
        variables: {
          input: {
            projectId,
            docId,
            invoiceId,
            dueDate: values.dueDate.format("YYYY-MM-DD"),
            deliveryOfServiceStart:
              values.deliveryOfServiceStart.format("YYYY-MM-DD"),
            deliveryOfServiceEnd:
              values.deliveryOfServiceEnd.format("YYYY-MM-DD"),
          },
        },
        context: { noErrorNotification: true },
      });

      enqueueSnackbar(t("Invoice opened"));
    } catch (error) {
      if (error instanceof ApolloError) {
        const code = error.graphQLErrors[0]?.extensions?.["code"];
        if (code === "E_INVOICE_PRECONDITIONS_NOT_MET") {
          const fields = error.graphQLErrors[0]?.extensions?.[
            "missingFields"
          ] as string[] | undefined;
          // The service period is a per-invoice field; the rest are
          // organisation settings. Present them separately so a mixed list
          // never directs the user to the org settings for the service period.
          const { servicePeriodMissing, orgFields } =
            splitMissingFields(fields);
          const messages: string[] = [];
          if (orgFields.length > 0) {
            const translated = orgFields
              .map(f => {
                const label =
                  MISSING_FIELD_LABELS[f as keyof typeof MISSING_FIELD_LABELS];
                return label ? t(label, { ns: "Invoices" }) : f;
              })
              .join(", ");
            messages.push(
              `${t("E-invoice could not be generated. Please configure in the organisation settings:", { ns: "Invoices" })} ${translated}`
            );
          }
          if (servicePeriodMissing) {
            messages.push(t(SERVICE_PERIOD_MISSING_KEY, { ns: "Invoices" }));
          }
          enqueueSnackbar(
            messages.join(" ") ||
              t(
                "E-invoice could not be generated. Please configure in the organisation settings:",
                { ns: "Invoices" }
              ),
            { variant: "error" }
          );
        } else if (code === "INVOICE_NO_LINE_ITEMS") {
          enqueueSnackbar(
            t(
              "An invoice needs at least one line item before it can be sent.",
              {
                ns: "Invoices",
              }
            ),
            { variant: "error" }
          );
        } else {
          enqueueSnackbar(t("An error occurred.", { ns: "Invoices" }), {
            variant: "error",
          });
        }
      } else {
        enqueueSnackbar(t("An error occurred.", { ns: "Invoices" }), {
          variant: "error",
        });
      }
    }
  };

  const button = (
    <Button
      color="primary"
      variant="contained"
      loading={isSubmittingOpenInvoice}
      startIcon={<SendIcon />}
      disabled={disabled}
      onClick={async () => {
        await handleSubmit({
          dueDate: moment().add(
            paymentTermDuration || DEFAULT_INVOICE_PAYMENT_TERM_DURATION,
            "seconds"
          ),
          deliveryOfServiceStart: deliveryOfServiceStart
            ? moment(deliveryOfServiceStart)
            : moment(),
          deliveryOfServiceEnd: deliveryOfServiceEnd
            ? moment(deliveryOfServiceEnd)
            : moment(),
        });
      }}
    >
      {t("Send to Client")}
    </Button>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip}>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
};

interface FormValues {
  dueDate: Moment;
  deliveryOfServiceStart: Moment;
  deliveryOfServiceEnd: Moment;
}
